import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Download } from 'lucide-react';
import { ReportHeader } from '../../components/ReportHeader';

const API = process.env.REACT_APP_BACKEND_URL;

// ── helpers ──────────────────────────────────────────────────────────────────

function fmt(n) {
  return (n ?? 0).toLocaleString();
}

function UtilBadge({ pct, allowedSeats }) {
  if (allowedSeats === 0 || allowedSeats === null || pct === null || pct === undefined) {
    return <span style={{ color: '#94a3b8', fontSize: 13 }}>—</span>;
  }
  let color, bg;
  if (pct >= 100)      { color = '#dc2626'; bg = '#fef2f2'; }
  else if (pct >= 75)  { color = '#d97706'; bg = '#fffbeb'; }
  else                 { color = '#16a34a'; bg = '#f0fdf4'; }

  return (
    <span
      style={{
        display: 'inline-block',
        background: bg,
        color,
        fontWeight: 700,
        fontSize: 13,
        padding: '2px 8px',
        borderRadius: 12,
        minWidth: 56,
        textAlign: 'center',
      }}
    >
      {pct.toFixed(1)}%
    </span>
  );
}

function Pagination({ page, totalPages, total, pageSize, onPage }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, page + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="pagination" data-testid="esi-pagination">
      <span className="pagination-info">
        {((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
      </span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {pages.map(p => (
        <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPage(p)}>{p}</button>
      ))}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

// ── main ─────────────────────────────────────────────────────────────────────

export default function EsiUtilisationReport() {
  const [stats, setStats]           = useState(null);
  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(false);
  const [exporting, setExporting]   = useState(false);

  // filter inputs (staged)
  const [search, setSearch]         = useState('');
  const [overLimit, setOverLimit]   = useState(false);
  // applied (committed) filters
  const [applied, setApplied]       = useState({ search: '', overLimit: false });

  const PAGE_SIZE = 50;

  // ── fetch stats (once) ──
  useEffect(() => {
    axios.get(`${API}/api/reports/esi-utilisation/stats`, { withCredentials: true })
      .then(r => setStats(r.data))
      .catch(console.error);
  }, []);

  // ── fetch list ──
  const fetchList = useCallback(async (pg, filters) => {
    setLoading(true);
    try {
      const params = { page: pg, page_size: PAGE_SIZE };
      if (filters.search)    params.search     = filters.search;
      if (filters.overLimit) params.over_limit = true;
      const r = await axios.get(`${API}/api/reports/esi-utilisation/list`,
        { params, withCredentials: true });
      setRows(r.data.data);
      setTotal(r.data.total);
      setTotalPages(r.data.total_pages);
      setPage(pg);
    } catch (e) { console.error(e); }
    finally     { setLoading(false); }
  }, []);

  // load on mount
  useEffect(() => { fetchList(1, { search: '', overLimit: false }); }, [fetchList]);

  const handleApply = () => {
    const f = { search, overLimit };
    setApplied(f);
    fetchList(1, f);
  };

  const handleReset = () => {
    setSearch('');
    setOverLimit(false);
    const f = { search: '', overLimit: false };
    setApplied(f);
    fetchList(1, f);
  };

  const handlePage = (pg) => fetchList(pg, applied);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (applied.search)    params.search     = applied.search;
      if (applied.overLimit) params.over_limit = true;
      const resp = await axios.get(`${API}/api/reports/esi-utilisation/export`,
        { params, withCredentials: true, responseType: 'blob' });
      const url  = URL.createObjectURL(resp.data);
      const link = document.createElement('a');
      link.href  = url;
      link.download = 'esi_utilisation.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally     { setExporting(false); }
  };

  // ── render ──
  return (
    <>
      <ReportHeader
        reportTitle="ESI Utilisation"
        primaryTable="domain_configuration"
      />
      <div data-testid="esi-utilisation-report" style={{ padding: '0 16px' }}>

      {/* ── Stat cards ── */}
      {stats && (
        <div className="stats-grid" data-testid="esi-stat-cards"
             style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', marginBottom: 24 }}>
          <div className="stat-card" data-testid="kpi-total-partners"
               style={{ borderTop: '3px solid #6366f1' }}>
            <div className="stat-label">Total Partners</div>
            <div className="stat-value">{fmt(stats.total_partners)}</div>
          </div>
          <div className="stat-card" data-testid="kpi-over-limit"
               style={{ borderTop: '3px solid #dc2626' }}>
            <div className="stat-label" style={{ color: '#dc2626' }}>Over Limit</div>
            <div className="stat-value" style={{ color: '#dc2626' }}>{fmt(stats.over_limit)}</div>
          </div>
          <div className="stat-card" data-testid="kpi-at-capacity"
               style={{ borderTop: '3px solid #d97706' }}>
            <div className="stat-label" style={{ color: '#d97706' }}>At Capacity (≥90%)</div>
            <div className="stat-value" style={{ color: '#d97706' }}>{fmt(stats.at_capacity)}</div>
          </div>
          <div className="stat-card" data-testid="kpi-avg-utilisation"
               style={{ borderTop: '3px solid #22c55e' }}>
            <div className="stat-label">Avg Utilisation</div>
            <div className="stat-value">
              {stats.avg_utilisation !== null ? `${Number(stats.avg_utilisation).toFixed(1)}%` : '—'}
            </div>
          </div>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="report-card" style={{ marginBottom: 16, padding: '14px 16px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 220px' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
              Search Partner Name
            </label>
            <input
              data-testid="esi-search"
              className="filter-input"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleApply()}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 2 }}>
            <input
              type="checkbox"
              id="over-limit-cb"
              data-testid="esi-over-limit-checkbox"
              checked={overLimit}
              onChange={e => setOverLimit(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor="over-limit-cb"
                   style={{ fontSize: 13, fontWeight: 500, color: '#374151', cursor: 'pointer' }}>
              Show over-limit only
            </label>
          </div>

          <div style={{ display: 'flex', gap: 8, paddingBottom: 2 }}>
            <button
              data-testid="esi-apply-btn"
              className="btn-primary"
              onClick={handleApply}
              style={{ fontSize: 13, padding: '7px 16px' }}
            >
              Apply
            </button>
            <button
              data-testid="esi-reset-btn"
              className="btn-secondary"
              onClick={handleReset}
              style={{ fontSize: 13, padding: '7px 16px' }}
            >
              Reset
            </button>
            <button
              data-testid="esi-export-btn"
              className="btn-secondary"
              onClick={handleExport}
              disabled={exporting}
              style={{ fontSize: 13, padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <Download size={13} />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Legend ── */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 10, flexWrap: 'wrap' }}>
        {[
          { label: '< 75%',  color: '#16a34a', bg: '#f0fdf4' },
          { label: '75–99%', color: '#d97706', bg: '#fffbeb' },
          { label: '≥ 100%', color: '#dc2626', bg: '#fef2f2' },
          { label: 'No limit set', color: '#94a3b8', bg: 'transparent' },
        ].map(({ label, color, bg }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{
              display: 'inline-block', width: 12, height: 12, borderRadius: 3,
              background: bg, border: '1.5px solid ' + color,
            }} />
            <span style={{ fontSize: 12, color: '#64748b' }}>{label}</span>
          </div>
        ))}
      </div>

      {/* ── Table ── */}
      <div className="report-card" data-testid="esi-table">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#64748b', fontSize: 14 }}>
            Loading…
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Partner Name</th>
                    <th>Partner Group</th>
                    <th style={{ textAlign: 'right' }}>Allowed Seats</th>
                    <th style={{ textAlign: 'right' }}>Registered</th>
                    <th style={{ textAlign: 'right' }}>Verified</th>
                    <th style={{ textAlign: 'right' }}>Utilisation %</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>
                        No results found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, i) => (
                      <tr key={row.partner_one_id ?? i}
                          data-testid={`esi-row-${row.partner_one_id}`}>
                        <td style={{ maxWidth: 280 }}>
                          <span title={row.esi_partner_name} style={{
                            display: 'block', overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {row.esi_partner_name || <span style={{ color: '#94a3b8' }}>—</span>}
                          </span>
                        </td>
                        <td>
                          {row.partner_group ? (
                            <span style={{
                              fontFamily: 'monospace', fontSize: 11,
                              background: '#f1f5f9', padding: '2px 6px', borderRadius: 4,
                            }}>
                              {row.partner_group}
                            </span>
                          ) : <span style={{ color: '#94a3b8' }}>—</span>}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {row.allowed_seats > 0 ? fmt(row.allowed_seats) : (
                            <span style={{ color: '#94a3b8', fontSize: 12 }}>No limit</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>{fmt(row.registered_learners)}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(row.verified_learners)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <UtilBadge pct={row.utilisation_pct} allowedSeats={row.allowed_seats} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              pageSize={PAGE_SIZE}
              onPage={handlePage}
            />
          </>
        )}
      </div>
      </div>
    </>
  );
}
